import Backbone from 'backbone'
import template from './data-source-selector.hbs'
import _ from 'lodash'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import i18n from 'scoreboard/components/i18n'
import FormView from 'scoreboard/components/form-view'
import license from 'scoreboard/components/license'
import togglePrefixedClass from 'scoreboard/components/toggle-prefixed-class'
import DatasetDataSourceForm from 'scoreboard/datasets/dataset-data-source-form'
import Dataset from 'scoreboard/datasets/model/dataset'
import metadata from 'scoreboard/components/metadata'
import StandardSourceSelector from './standard-source-selector'
import { joinThresholdId, splitDataPropertyOption } from 'scoreboard/charts/data-property'
import * as adminLink from 'scoreboard/components/admin-link'
import isGeographicDataType from '../is-geographic-data-type'
import isGeographicOrRollup from '../../datasets/is-geographic-or-rollup'

const NONE = 'none'

import DomainType from 'scoreboard/components/domain-type'
const { SCORECARD_ITEM, INITIATIVE, DATASET } = DomainType

const DataSourceSelector = Backbone.View.extend({
	tagName: 'form',
	className: 'set-data-source',

	/**
	 * @param {Object} options - in addition to specific options documented below,
	 * the entire 'opt' object is passed along to section-specific components,
	 * like 'scoreboard/datasets/data-source-form'
	 *
	 * @param {DomainType} domainType - the initially selected domain type
	 * ('scorecard-item', 'dataset', etc). If null, the "None" option will be
	 * selected.
	 *
	 * @param {Object} dataSource - the initially selected data source settings.
	 * Exact format of the object depends on the section. For example, 'datasets'
	 * will be a JS form of DatasetAggregation
	 *
	 * @param {Object} datasets - datasets objects (not Dataset models) keyed
	 * by ID that are needed for the initially selected data source settings
	 *
	 * @param {Object} scorecards - scorecard objects (not ScorecardNode models) keyed
	 * by ID that are needed for the initially selected data source settings
	 *
	 * @param {Object} initiatives - initiative objects (not Initiative models) keyed
	 * by ID that are needed for the initially selected data source settings
	 *
	 * @param {Boolean} onlyDatasets - if true, only the datasets option is available
	 *
	 * @param {Boolean} hideRollupFields - shows or hides rollup fields in the field
	 * dropdown when working with datasets
	 *
	 * @param {Boolean} [options.showAggregationType] whether the
	 * aggregation type dropdown should be displayed
	 *
	 * @param {Boolean} [options.allowCalculated=true] whether calculated data
	 * sources are allowed
	 *
	 * @param {String} [options.datasetErrorCode] #43276 if a dataset is selected
	 * that has caused an error but the dataset is not in an error state, it can
	 * be passed through here
	 * @param {String} [options.defaultDatasetDateAggregationType] when selecting a date
	 * field for a dataset, this is the aggregation type to default to
	 *
	 * @param {Boolean} [options.showNone=false] whether the "None" domain type option
	 * should be shown
	 *
	 * @param {Object} objectSelectorOptions - options to be passed along to ObjectSelectorMenu
	 *
	 * @param {Object} [periodSelector] - a Backbone.View to be rendered below
	 * the data source selector, which allows the user to select a calendar period.
	 * Note that this is generic, because the logic for rendering the period
	 * selector and handling its data is context-specific. For example, shape
	 * widgets have a lot of widget-specific logic that doesn't apply to this
	 * general-purpose data source selector (eg. this is also used in charts)
	 *
	 * @param {String[]} [options.allowedDomainTypes=[SCORECARD_ITEM, INITIATIVE, DATASET]]
	 * the domain types that are allowed to be selected
	 *
	 * @param {Boolean} [options.includeTotal=true] indicates that "Total X"
	 * options may be shown in the select if they were previously added as visible
	 * linked fields.
	 *
	 * @param {Boolean} [options.includeNativeTotal=true] whether
	 * to add a "Number of X" option for the native root dataset
	 *
	 * @param {Function} [options.restrictTemporalToNumUnique=false] indicates that
	 * temporal data types (date, time, and date/time) should be restricted to
	 * the number of unique aggregation type. This is useful for spots like
	 * gauges which can't reasonable show dates or times.
	 *
	 * @param {String} [options.warningBannerMessage] a message to display in
	 * a warning banner if needed (already i18n'd)
	 *
	 * @param {Boolean} [options.includeEditFields=true] whether to include the
	 * edit fields in the data source form field dropdown
	 *
	 * @param {Boolean} [options.allowListUniqueValues=true] indicates that the
	 * "List Unique Values" aggregation type should be allowed
	 */
	initialize: function (options) {
		this.options = options
		this.isPlaceholder = this.options.domainType === 'chart-placeholder'
		this.allowCalculated = _.get(options, 'allowCalculated', true)
		this.originalDatasetId = _.get(options, 'dataSource.startingDatasetId')
		this.includeEditFields = _.get(options, 'includeEditFields', true)

		this.initializeFormView()
		this.initializeAllowedDomainOptions()
	},

	initializeFormView: function() {
		const self = this
		let SelectorFormView = FormView.extend({
			test: this.test.bind(this),
			// Needs to be a normal function so `this` is in context of FormView
			submit: function() {
				if (self.showingError) return false
				return FormView.prototype.submit.apply(this, arguments)
			}
		})

		this.formView = new SelectorFormView({
			el: this.el,
			submit: this.save.bind(this)
		})

		// #42319 Watch for changes to make sure we keep the submit button disabled
		// if the selected dataset is still invalid
		this.formView.on('change', this.updateErrorBanner.bind(this))
	},

	initializeAllowedDomainOptions: function() {
		const allowedDomainTypes = this.options?.allowedDomainTypes ??
			[ SCORECARD_ITEM, INITIATIVE, DATASET ]
		this.allowedDomainOptions = []
		_.forEach(allowedDomainTypes, domainType => {
			let meta = metadata[domainType]
			if (license[meta.section]) {
				this.allowedDomainOptions.push({
					value: domainType,
					label: i18n.get(meta.singularCode)
				})
			}

		})

		if (this.options.showNone) {
			this.allowedDomainOptions.push({
				value: NONE,
				label: i18n.get('common.none')
			})
		}
	},

	render: function () {
		this.$el.html(template())

		if (this.options.datasetsOnly) {
			this.$('.section-toggle-container').toggleClass('is-not-displayed', true)
			this._renderDatasetSelector()
		} else {
			this.$('.section-toggle-container').toggleClass('is-not-displayed', false)
			this._renderSectionToggle()
			this._renderSelector(this.options)
		}

		this.formView.start()

		return this
	},

	_getDomainType: function(domainType) {
		if (_.isNil(domainType)) {
			// LabeledToggle needs a non-null value to work properly, so use
			// 'none' internally (we'll still work with null for input/output)
			return NONE
		}

		return domainType === 'chart-placeholder' ? this.allowedDomainOptions[0].value
			: domainType
	},

	_renderSectionToggle: function () {
		this.sectionToggle = new LabeledToggle({
			value: this._getDomainType(this.options.domainType),
			options: this.allowedDomainOptions
		})
		this.sectionToggle.on('change', this._onSectionChange.bind(this))

		this.$('.section-toggle').append(this.sectionToggle.el)
		this.sectionToggle.render()

		this._updateSectionClass(this.sectionToggle.val())
	},

	_renderSelector: function (opt) {
		let domainType = this._getDomainType(opt.domainType)

		this.datasetDataSourceForm?.remove()
		delete this.datasetDataSourceForm

		this.standardSourceSelector?.remove()
		delete this.standardSourceSelector

		if (domainType === SCORECARD_ITEM || domainType === INITIATIVE) {
			this._renderStandardSelector(this.options.dataSource)
		} else if (domainType === DATASET) {
			this._renderDatasetSelector()
		}

		this._renderPeriodSelector()
	},

	_renderPeriodSelector() {
		let showPeriodSelector = this.options.periodSelector
			&& [SCORECARD_ITEM, INITIATIVE].includes(this.sectionToggle.val())

		this.$('.period-selector-container')
			.toggleClass('is-not-displayed', !showPeriodSelector)

		// #57457
		// `this.periodSelector` is a `widget-period-selector.js` object.
		// That module will append a new `periodSelector` element
		// every time its render function is called. To avoid creating
		// duplicates, and to preserve the chosen settings, return early
		// if the periodSelector has already been rendered.
		if (!showPeriodSelector || this.periodSelector?.periodSelector) {
			return
		}

		this.periodSelector = this.options.periodSelector
		this.$('.period-selector-container').append(this.periodSelector.el)
		this.periodSelector.render()
	},

	_renderDatasetSelector: function () {
		let datasetId = this.options.dataSource.startingDatasetId ??
			this.options.dataSource.datasetId
		let dataset
		if (datasetId) {
			dataset = new Dataset(this.options.datasets[datasetId])
		}

		let FormConstructor = DatasetDataSourceForm.extend({
			allowPercentageAggregationType: this.options.allowPercentageAggregationType,
			showDecimalPrecision: false
		})

		this.datasetDataSourceForm = new FormConstructor(_.extend({
			model: dataset,
			formView: this.formView,
			hideRollupFields: this.options.hideRollupFields,
			allowCalculated: this.allowCalculated,
			defaultDateAggregationType: this.options.defaultDatasetDateAggregationType,
			includeEditFields: this.includeEditFields
		}, this.options))
		this.datasetDataSourceForm.on('dataset-selected', this.showOrHideErrorStateBanner.bind(this))
		let detailsContainer = this._detailsContainer()
		detailsContainer.removeClass('panel')
			.append(this.datasetDataSourceForm.el)
		this.datasetDataSourceForm.render()

		this.selectedDataset = dataset || null

		// This is called before the dialog has actually opened, so the banner
		// won't be available yet to attach the error to
		process.nextTick(() => this.showOrHideErrorStateBanner(dataset))
	},

	_renderStandardSelector: function (dataSource) {
		let domainType = this.sectionToggle.val()
		// #40241 do not display basic/calculated type toggle for scorecards
		// or initiatives
		let disallowTypeToggle = _.includes([INITIATIVE, SCORECARD_ITEM],
			domainType)

		// #45853 for charts we don't support calculated for scorecards or
		// initiatives, so clean up the dataSource attributes in case we are
		// switching away from a calculated dataset dataSource
		dataSource = _.omit(dataSource, 'equation', 'dataType',
			'aggregationType', 'filters', 'aggregations')
		// and set dataSourceType to basic so the equation editor doesn't show
		dataSource.dataSourceType = 'basic'

		let selectorOptions = _.extend({}, this.options, {
			domainType,
			dataSource,
			allowDomainObjectSelection: true,
			disallowTypeToggle
		})

		if (domainType === SCORECARD_ITEM) {
			// #40288 format the selected option so that it can be set on the
			// dropdown correctly
			_.set(selectorOptions, 'dataSource.dataProperty',
				this._prepareSelectedValue(dataSource))
		}
 		this.standardSourceSelector = new StandardSourceSelector(selectorOptions)
		this._detailsContainer().append(this.standardSourceSelector.el)
		this.standardSourceSelector.render()

		// Listen for change events and update the FormView. Note that FormView
		// is already listening for DOM change events on "regular" inputs
		this.listenTo(this.standardSourceSelector,
			'change:domainObject change:equation change:dataSourceType',
			() => {
				this.formView.change()
				this.formView.clearErrors()
			})
	},

	/**
	 * Formats the passed in selected value so that it matches the format of the
	 * dropdown options
	 */
	_prepareSelectedValue: function(dataSource) {
		if (!dataSource.dataProperty) return undefined

		const scorecard = this.options.scorecards?.[dataSource.domainObjectId]
		if (!scorecard) return dataSource.dataProperty

		const scoringTypeId = scorecard.scoringTypeId
		const dataProperty = splitDataPropertyOption(dataSource.dataProperty)
		// If there is no thresholdIndex, then the selected option is not
		// a threshold
		if (!_.isNil(dataProperty.thresholdIndex)) {
			return joinThresholdId(dataProperty.thresholdIndex, scoringTypeId)
		}
		return dataProperty.dataProperty
	},

	_detailsContainer: function () {
		return this.$('.details-container')
	},

	_onSectionChange: function () {
		var domainType = this.sectionToggle.val()

		this._renderSelector({ domainType })

		this.formView.change()

		this._updateSectionClass(domainType)

		// #40291 only show the banner if we are on the datasets tab
		if (domainType === DATASET) {
			this.showOrHideErrorStateBanner(this.selectedDataset)
		} else {
			this.formView.clearErrors()
		}
	},

	updateErrorBanner: function(e) {
		if (e) {
			// Only do this if formView.change wasn't called manually
			this.showOrHideErrorStateBanner(this.selectedDataset)
		}
	},

	/**
	 * #40291 For dataset data sources, this will render an error banner if the
	 * selected dataset is in a bad state
	 */
	showOrHideErrorStateBanner: function(dataset) {
		this.selectedDataset = dataset || null
		const selectedDatasetId = _.get(dataset, 'id', null)
		const errorCode = this.options.datasetErrorCode
		const usingOriginalDataset = selectedDatasetId !== null && selectedDatasetId === this.originalDatasetId
		// clear the form errors if:
		// * no dataset is selected
		// * the dataset doesn't have an error code
		// * an error code wasn't passed into this component OR
		//   the selected dataset is different than the one passed in
		if (!dataset || !dataset.get('errorCode') && (!errorCode || !usingOriginalDataset)) {
			this.formView.clearErrors()
			this.formView.change() // #41823
			this.showingError = false
			_.invoke(this.datasetDataSourceForm, 'setInvalidDataset', false)
			return
		}

		// #39326 if the dataset is in an error state, show the error
		let msgCode = dataset.get('errorCode')
		let link = null
		if (msgCode === 'charts.error.treeMapsNeedRollups') {
			link = adminLink.adminDatasetRollupTreesLink()
		} else {
			msgCode = 'report.designer.dataset.selected.error'
		}

		if (!msgCode) {
			// Else if an error is passed in, show that
			msgCode = errorCode
		}

		this.showingError = true
		_.invoke(this.datasetDataSourceForm, 'setInvalidDataset', true)
		this.formView.errorMessage(i18n.get(msgCode, link))
	},

	// Append relevant classes to this.el depending on the section selected
	_updateSectionClass: function (domainType) {
		let section = domainType === NONE
			? NONE
			: metadata[domainType].section
		togglePrefixedClass(this.el, 'section', section)
	},

	section: function () {
		return this.sectionToggle.val()
	},

	test: function () {
		if (this.datasetDataSourceForm) {
			const requiredDataType = this.options.requiredDataType
			if (requiredDataType && this.selectedDataset) {
				let vlfs = _(this.selectedDataset.visibleLinkedFieldsById)
					.values()
					.map(f => this.selectedDataset.combinedFieldsById[f.fieldId])
					.value()
				const fields = _.concat(this.selectedDataset.get('fields'), vlfs)
				let hasRequiredDataType = _.some(fields, field => {
					let underlyingField = this.selectedDataset.getUnderlyingField(field)
					return requiredDataType === 'geographic' ?
						isGeographicDataType(underlyingField.dataType) :
						underlyingField.dataType === requiredDataType
				})
				if (!hasRequiredDataType) {
					const msgCode =
						`charts.error.missingRequiredDataType.${requiredDataType}`
					return isGeographicOrRollup(requiredDataType) ?
						i18n.get(msgCode, adminLink.adminDatasetRollupTreesLink()) :
						i18n.get(msgCode)
				}
			}
			// #49342 also invoke datasetDataSourceForm test method to make sure
			// user selections are valid
			return _.invoke(this.datasetDataSourceForm, 'test')
		}

		// #46236 since the user may have switched between scorecards/initiatives,
		// make sure that a domain object was actually selected
		if (this.standardSourceSelector) {
			if (_.isNil(this.standardSourceSelector.selectedDomainObject())) {
				let domainType = this.sectionToggle.val()
				let key = 'common.error.mustSelect.' + domainType
				return i18n.get(key)
			}
		}
	},

	save: function () {
		if (this.datasetDataSourceForm) {
			// Validation is done asynchronously when primary button is clicked
			// (specifically an XHR for equation validation), so we have to
			// listen for the 'done' event
			this.listenTo(this.datasetDataSourceForm, 'done', (dataSource, dataset) => {
				// #58058 the datasetDataSourceForm refers to the dataset ID as
				// startingDatasetId right now, but previously it was named
				// datasetId. To be resilient to change, let's set the
				// datasetId property explicitly based on the dataset
				// #58822 dataset will be nil for calculated chart series
				dataSource.datasetId = dataset?.id ?? dataSource.datasetId
				this.trigger('done', {
					wasPlaceholder: this.isPlaceholder,
					domainType: DATASET,
					dataSource,
					dataset,
					datasetsById: this.datasetDataSourceForm.datasetsById
				})
			})

			this.datasetDataSourceForm.submit()
		} else if (this.standardSourceSelector) {
			this.trigger('done', {
				wasPlaceholder: this.isPlaceholder,
				domainType: metadata[this.section()].domainType,
				dataSource: this.standardSourceSelector.val(),
				period: this.periodSelector?.val()
			})
		} else {
			this.trigger('done', {
				wasPlaceholder: this.isPlaceholder,
				domainType: null
			})
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([
			this.sectionToggle, this.formView, this.datasetDataSourceForm,
			this.standardSourceSelector
		], 'remove')
	}
})

export default DataSourceSelector

DataSourceSelector.__filename__ = 'components/data-source-selector/data-source-selector.js'
